declare const htmx: { process(elt: Element): void };

class DialogWrapper {
    private cleanSnapshot = '';

    constructor(private dialog: HTMLDialogElement) {
        // Event delegation for close buttons — survives any HTMX swap
        dialog.addEventListener('click', (e) => {
            if ((e.target as Element).closest('[data-close]')) {
                this.close();
            }
        });

        // Block Escape before the close watcher sees it — preventDefault on
        // `cancel` only works for the first press; subsequent ones bypass it.
        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.dialog.hasAttribute('no-escape-key')) {
                e.preventDefault();
                e.stopPropagation();
            }
        }, true);

        // Intercept Escape key to go through our animation path
        dialog.addEventListener('cancel', (e) => {
            e.preventDefault();
            this.close();
        });

        // Finalize close after animation completes
        dialog.addEventListener('animationend', () => {
            if (!dialog.classList.contains('closing')) return;
            dialog.classList.remove('closing');
            dialog.close();
            this.reset();
        });

        (dialog as any)['hsm-wrapper'] = this;

        dialog.setAttribute('hsm-wrapper-attached', '');
    }

    open(data?: Record<string, string>) {
        if (data) {
            for (const [key, value] of Object.entries(data)) {
                const el = this.dialog.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`[name="${key}"]`);
                if (!el) continue;
                if (el.tagName === 'SELECT') {
                    (el as HTMLSelectElement).selectedIndex =
                        Array.from((el as HTMLSelectElement).options).findIndex(o => o.value === value);
                } else {
                    el.value = value;
                }
            }
        }

        this.snapshot();
        this.dialog.showModal();
    }

    close() {
        this.dialog.classList.add('closing');
    }

    private snapshot() {
        this.cleanSnapshot = this.dialog.innerHTML;
    }

    private reset() {
        if (this.dialog.hasAttribute('no-reset')) {
            return;
        }
        this.dialog.innerHTML = this.cleanSnapshot;
        htmx.process(this.dialog);
    }
}

export function initDialogs() {
    document.querySelectorAll<HTMLDialogElement>('dialog').forEach(dialog => {
        const wrapper = new DialogWrapper(dialog);

        if (dialog.id) {
            document.querySelectorAll(`[data-dialog="${dialog.id}"]`).forEach(trigger => {
                trigger.addEventListener('click', () => wrapper.open());
            });
        }
    });

    // Servers can close the open modal via an `HX-Trigger: close-dialog` response
    // header (the event bubbles up to body). Routes through the animation/reset path.
    document.body.addEventListener('close-dialog', () => {
        const open = document.querySelector<HTMLDialogElement>('dialog[open]');
        (open as any)?.['hsm-wrapper']?.close();
    });

    // Allow dialogs that are added dynamically after page load (such as from the Angular app)
    // to trigger initialization of the dialog by firing this event.
    document.body.addEventListener('init-dialog', (event: CustomEvent<HTMLDialogElement>) => {
        const dialog = event.detail;
        if (dialog.getAttribute('hsm-wrapper-attached') === null) {
            const wrapper = new DialogWrapper(dialog);

            if (dialog.id) {
                document.querySelectorAll(`[data-dialog="${dialog.id}"]`).forEach(trigger => {
                    trigger.addEventListener('click', () => wrapper.open());
                });
            }
        }
    });
}
